import { useCallback, useEffect, useMemo, useState } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import TaskHeader from "components/task/header/TaskHeader";
import { Box, Skeleton, Stack, Typography } from "@mui/joy";
import { taskListAtom } from "recoil/taskListAtom";
import { supplementaryTaskListAtom } from "recoil/supplementaryTaskListAtom";
import { progressOpenMapAtom } from "recoil/progressOpenMapAtom";
import { TaskMetaData } from "data/programData";
import { Status } from "components/task-list/TaskItemTemplate";
import { theme } from "styles/theme";
import { LockIconSvg, UpArrowSvg } from "static/image/common/SvgAssets";
import useOpenTask from "hooks/useOpenTask";
import TabButtonList from "components/common/TabButtonList";
import { useQueries } from "@tanstack/react-query";
import { patientAtom } from "recoil/patientAtom";
import useIsInappWebview from "hooks/useIsInappWebview";
import useBottomSafePadding from "hooks/useBottomSafePadding";
import TestResult from "components/dashboard/TestResult";
import MyConcernAndGoal from "components/dashboard/MyConcernAndGoal";
import {
  CONCEPT_DOMAIN,
  CONCEPT_NOTE_CONCERN_AND_GOAL_DETAIL_ENDPOINT,
  conceptApi,
  ConcernAndGoal,
} from "api/conceptApi";
import {
  DASHBOARD_DETAIL_ENDPOINT,
  DASHBOARD_DOMAIN,
  dashboardApi,
  DashboardType,
  TestResultType,
} from "api/dashboardApi";
import { taskApi } from "api/taskApi";
import { userAtom } from "../../../recoil/userAtom";

type ProgressItemProps = {
  taskKey?: string;
  isDone: boolean;
  isOpen: boolean;
  locked: boolean;
  title: string;
};

function ProgressItemRow({
  taskKey,
  isDone,
  isOpen,
  locked,
  title,
}: ProgressItemProps) {
  const openTask = useOpenTask({ taskKey: taskKey || "" });
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        px: 1.5,
        height: "40px",
        borderRadius: "12px",
        cursor: !locked && taskKey ? "pointer" : "default",
        "@media (min-width: 1024px)": {
          "&:hover": {
            backgroundImage: `linear-gradient(${theme.vars.palette.hoverLight}, ${theme.vars.palette.hoverLight})`,
          },
        },
        "@media (max-width: 1024px)": {
          "&:hover": {
            backgroundColor: theme.vars.palette.Surface.Forward,
          },
        },
        "&:active": {
          backgroundImage: `linear-gradient(${theme.vars.palette.hoverLight}, ${theme.vars.palette.hoverLight})`,
        },
        "&:focus": {
          backgroundColor: theme.vars.palette.Surface.Forward,
        },
      }}
      onClick={() => {
        if (!locked && taskKey) {
          openTask();
        }
      }}
    >
      {locked ? (
        <LockIconSvg color={theme.vars.palette.Content.SuperWeak} />
      ) : (
        <Status isDone={isDone} isOpen={isOpen} />
      )}
      <Typography
        variant="Body"
        sx={{
          px: 0,
          flex: 1,
          fontWeight: 500,
          color: locked
            ? theme.vars.palette.Content.Weak
            : theme.vars.palette.Content.Strong,
        }}
      >
        {title}
      </Typography>
    </Stack>
  );
}

export default function MyProgressV2() {
  const taskListState = useRecoilValue(taskListAtom);
  const patientState = useRecoilValue(patientAtom);
  const userState = useRecoilValue(userAtom);

  const [selectedTab, setSelectedTab] = useState<string>("progress");
  const isInappWebview = useIsInappWebview();
  // Android in-app 에서 마지막 항목이 home indicator 에 직접 닿지 않도록
  // breathing 확보. iOS / PC 는 상위 컨테이너 env() 로 처리되므로 0 반환.
  const bottomPb = useBottomSafePadding("0px");
  const [openMap, setOpenMap] = useRecoilState(progressOpenMapAtom);
  const isTaskListLoading = !taskListState || taskListState.length === 0;
  const setSupplementaryList = useSetRecoilState(supplementaryTaskListAtom);

  const programType = patientState?.programType ?? userState?.programType;
  const isProgramTypeB = programType === "b";

  const isStarterPlan = patientState?.plan === "starter";
  const isAllAccessPricingUserExpired =
    userState?.role === "patient" &&
    userState?.pricingModel === "c" &&
    !userState?.subscription?.isSubscribing &&
    userState?.subscription?.hasStarted;

  const tabsData = useMemo(() => {
    const base = [
      {
        id: "progress",
        label: "진도",
        onClick: () => setSelectedTab("progress"),
      },
      {
        id: "goal",
        label: "목표",
        onClick: () => setSelectedTab("goal"),
      },
    ];
    if (!isStarterPlan) {
      base.push({
        id: "trend",
        label: "추이",
        onClick: () => setSelectedTab("trend"),
      });
    }
    return base;
  }, [isStarterPlan, setSelectedTab]);

  useEffect(() => {
    if (isStarterPlan && selectedTab === "trend") {
      setSelectedTab("progress");
    }
  }, [isStarterPlan, selectedTab]);

  const sections: {
    title: string;
    items: { taskId: string; title: string }[];
  }[] = useMemo(
    () => [
      {
        title: "1. 들어가며",
        items: isProgramTypeB
          ? [
              { taskId: "10-1-A", title: "변화의 시작" },
              { taskId: "10-2-A", title: "거리두기란 무엇인가" },
              { taskId: "10-3-A", title: "목표 설정하기" },
              { taskId: "12-1-A", title: "복습: 거리두기에 대하여" },
              { taskId: "1-3-A", title: "생각과 팝콘과 나" },
              { taskId: "8-1-A", title: "생각과 다시 관계 맺기" },
            ]
          : [
              { taskId: "1-1-A", title: "나의 고민과 목표" },
              { taskId: "1-2-A", title: "프로그램 구성하기" },
              { taskId: "1-3-A", title: "생각과 팝콘과 나" },
              { taskId: "8-1-A", title: "생각과 다시 관계 맺기" },
            ],
      },
      {
        title: "2. 알아차리기",
        items: [
          ...(isProgramTypeB
            ? [
                { taskId: "11-1-A", title: "인지하기란 무엇인가" },
                { taskId: "14-1-A", title: "주의 통제 훈련" },
              ]
            : []),
          { taskId: "8-2-A", title: "첫 번째 명상 훈련" },
          { taskId: "8-10-A", title: "레몬 맛 팝콘" },
          { taskId: "8-3-A", title: "두 번째 명상 훈련" },
          { taskId: "8-4-A", title: "세 번째 명상 훈련" },
          { taskId: "8-5-A", title: "네 번째 명상 훈련" },
          { taskId: "8-6-A", title: "훈련 루틴 만들기" },
        ],
      },
      {
        title: "3. 분리하기",
        items: isProgramTypeB
          ? [
              { taskId: "11-2-A", title: "경험 분리 훈련" },
              { taskId: "11-3-A", title: "경험 분리 훈련 - 상황" },
              { taskId: "12-2-A", title: "경험 분리 훈련 - 생각" },
              { taskId: "12-3-A", title: "경험 분리 훈련 - 감정/신체 반응" },
              { taskId: "13-1-A", title: "경험 분리 훈련 - 행동" },
              { taskId: "13-2-A", title: "복습: 경험 분리에 대하여" },
              { taskId: "8-8-A", title: "감정과 신체 다스리기" },
              { taskId: "8-9-A", title: "생각에서 빠져나오기" },
            ]
          : [
              { taskId: "2-3-A", title: "꾸러미 분리하기" },
              { taskId: "2-4-A", title: "분리 연습하기" },
              { taskId: "8-8-A", title: "감정과 신체 다스리기" },
              { taskId: "8-9-A", title: "생각에서 빠져나오기" },
            ],
      },
      {
        title: "4. 따져보기",
        items: [
          { taskId: "3-2-A", title: "자동적인 생각" },
          { taskId: "4-1-A", title: "생각함정" },
          { taskId: "4-4-1-A", title: "생각함정 1. 책임 과다" },
          { taskId: "4-4-2-A", title: "생각함정 2. 근거 부족" },
          { taskId: "4-4-3-A", title: "생각함정 3. 부정 편향" },
          { taskId: "4-4-4-A", title: "생각함정 4. 평가" },
          { taskId: "4-4-5-A", title: "생각함정 5. 개념화" },
          { taskId: "4-4-6-A", title: "생각함정 6. 반추" },
        ],
      },
      {
        title: "5. 기꺼이 경험하기",
        items: [
          { taskId: "9-1-A", title: "마음과 문제 해결" },
          { taskId: "9-2-A", title: "나의 지난 노력들" },
          { taskId: "9-3-A", title: "기꺼이 경험하기" },
          { taskId: "9-4-A", title: "적극적 관찰자 되기 - 상황" },
          { taskId: "9-6-A", title: "적극적 관찰자 되기 - 감정" },
          { taskId: "9-8-A", title: "적극적 관찰자 되기 - 신체 반응" },
          { taskId: "9-10-A", title: "적극적 관찰자 되기 - 행동" },
          { taskId: "9-12-A", title: "몸의 감각 느끼기" },
          { taskId: "9-13-A", title: "어려움을 기꺼이 경험하기" },
        ],
      },
      {
        title: "6. 가치 전념하기",
        items: [
          { taskId: "7-1-A", title: "긍정 기록 시작하기" },
          { taskId: "7-3-A", title: "삶의 방향 선택하기" },
          { taskId: "7-5-A", title: "가치 행동" },
        ],
      },
      {
        title: "7. 그물 이해하기",
        items: [
          { taskId: "6-1-A", title: "생각 그물" },
          { taskId: "6-2-A", title: "중간 믿음" },
          { taskId: "6-4-A", title: "핵심 믿음" },
          { taskId: "6-7-A", title: "핵심 경험" },
          { taskId: "6-10-A", title: "대처 전략과 중간 믿음" },
          { taskId: "6-9-A", title: "생각 그물 이해하기" },
          { taskId: "6-11-A", title: "생각 그물과 가치" },
          { taskId: "6-13-A", title: "생각과 거리두고 가치로 나아가기" },
        ],
      },
    ],
    [isProgramTypeB]
  );

  useEffect(() => {
    // Wait for task list to load before initializing openMap
    if (!taskListState || taskListState.length === 0) {
      return;
    }
    // Initialize with all sections closed on first load only
    setOpenMap((prev) => {
      // If already initialized, keep user's current open/closed state
      if (Object.keys(prev).length > 0) {
        return prev;
      }
      const next: Record<string, boolean> = {};
      sections.forEach((s) => {
        next[s.title] = false;
      });
      return next;
    });
  }, [sections, taskListState]);

  const getTitleFromMeta = (taskId: string) =>
    TaskMetaData.find((t) => t.taskId === taskId)?.title || "";

  // Data fetching for 목표(Concern & Goal) and 추이(Self-check results)
  const [dashboardData, setDashboardData] = useState<
    DashboardType | undefined
  >();
  const [concernAndGoal, setConcernAndGoal] = useState<
    ConcernAndGoal | undefined
  >(undefined);

  const concernAndGoalQueryFn = useCallback(
    () => conceptApi.noteConcernAndGoalDetail(patientState?.patientId!!),
    [patientState]
  );

  const dashBoardQueryFn = useCallback(
    () => dashboardApi.load(patientState?.patientId!!),
    [patientState]
  );

  const results = useQueries({
    queries: [
      {
        enabled: !!patientState?.patientId,
        queryKey: [
          CONCEPT_DOMAIN,
          CONCEPT_NOTE_CONCERN_AND_GOAL_DETAIL_ENDPOINT,
          patientState?.patientId,
        ],
        queryFn: concernAndGoalQueryFn,
      },
      {
        enabled: !!patientState?.patientId,
        queryKey: [
          DASHBOARD_DOMAIN,
          DASHBOARD_DETAIL_ENDPOINT,
          patientState?.patientId,
        ],
        queryFn: dashBoardQueryFn,
      },
    ],
  });

  const concernAndGoalResult = results[0]?.data?.result;
  const dashBoardResult = results[1]?.data?.result;

  useEffect(() => {
    if (concernAndGoalResult) {
      setConcernAndGoal(concernAndGoalResult);
    }
  }, [patientState, concernAndGoalResult]);

  useEffect(() => {
    if (dashBoardResult) {
      setDashboardData(dashBoardResult);
    }
  }, [patientState, dashBoardResult]);

  useEffect(() => {
    if (!patientState?.patientId) {
      setDashboardData({
        concept: {},
        history: {},
        selfCheckResult: {},
      } as unknown as DashboardType);
    }
  }, [patientState]);

  // Initialize supplementary task list on entry and when patient changes
  useEffect(() => {
    let active = true;
    if (!patientState?.patientId) {
      setSupplementaryList([]);
      return;
    }
    taskApi
      .getSupplementaryList(patientState.patientId)
      .then((res) => {
        if (!active) return;
        const list = res?.result ?? res?.data ?? [];
        setSupplementaryList(list);
      })
      .catch(() => {
        if (!active) return;
        setSupplementaryList([]);
      });
    return () => {
      active = false;
    };
  }, [patientState, patientState?.patientId, setSupplementaryList]);

  return (
    <>
      <Box sx={{ position: "sticky", top: 0, zIndex: 100 }}>
        <TaskHeader taskKey={"progress"} />
      </Box>

      <Box className="relative w-full px-[20px] py-[8px]" sx={{ zIndex: 99 }}>
        <Stack className="w-full max-w-[660px] mx-auto">
          <TabButtonList selectedTab={selectedTab} data={tabsData} />
        </Stack>
      </Box>

      <div
        className="w-full h-full overflow-y-auto overflow-x-hidden px-[20px] scrollbar-hide"
        style={{
          overscrollBehavior: "none",
          WebkitOverflowScrolling: "auto",
          paddingBottom: bottomPb,
        }}
      >
        <Box className="relative w-full max-w-[660px] mx-auto">
          <Stack spacing={1.5}>
            {selectedTab === "progress" && (
              <Stack
                sx={{
                  width: "100%",
                }}
                spacing={1}
              >
                {isTaskListLoading && (
                  <Stack spacing={1}>
                    {[1, 2, 3].map((i) => (
                      <Box
                        key={i}
                        sx={{
                          borderRadius: "16px",
                          px: "20px",
                          py: "18px",
                          backgroundColor: theme.vars.palette.Surface.Forward,
                        }}
                      >
                        <Stack spacing={1}>
                          <Skeleton
                            variant="rectangular"
                            height={28}
                            sx={{ borderRadius: "8px" }}
                          />
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                )}
                {!isTaskListLoading &&
                  sections.map((section) => (
                    <Box
                      key={section.title}
                      sx={{
                        borderRadius: "16px",
                        px: "20px",
                        py: "14px",
                        backgroundColor: theme.vars.palette.Surface.Forward,
                      }}
                    >
                      <Stack>
                        {(() => {
                          const total = section.items.length;
                          const done = section.items.reduce(
                            (acc, { taskId }) => {
                              const candidates = taskListState.filter(
                                (t) => t.taskId === taskId
                              );
                              const task =
                                candidates.find((t) => t.isOpen) ||
                                candidates[0];
                              return acc + (task?.isDone ? 1 : 0);
                            },
                            0
                          );
                          const percent =
                            total > 0 ? Math.round((done / total) * 100) : 0;
                          const isOpenSection = !!openMap[section.title];
                          return (
                            <Stack
                              direction="row"
                              spacing="8px"
                              alignItems="center"
                              onClick={() =>
                                setOpenMap((m) => ({
                                  ...m,
                                  [section.title]: !isOpenSection,
                                }))
                              }
                              sx={{ cursor: "pointer", height: "36px" }}
                            >
                              <Box
                                sx={{
                                  ...(!isOpenSection && {
                                    transform: "rotate(-90deg)",
                                    transformOrigin: "center",
                                  }),
                                  transition: "transform 0.2s",
                                }}
                              >
                                <UpArrowSvg
                                  color={theme.vars.palette.Content.Strong}
                                />
                              </Box>
                              <Typography
                                variant={"Body"}
                                sx={{
                                  px: 0,
                                  fontWeight: 700,
                                  color: theme.vars.palette.Content.Strong,
                                }}
                              >
                                {section.title}
                              </Typography>
                              <Box sx={{ flex: 1 }} />
                              <Typography
                                variant="Body"
                                sx={{
                                  color: theme.vars.palette.Content.Weak,
                                  fontWeight: 500,
                                }}
                              >
                                {`${percent}%`}
                              </Typography>
                            </Stack>
                          );
                        })()}
                        {openMap[section.title] && (
                          <Stack sx={{ pl: 2 }}>
                            {section.items.map(({ taskId, title }) => {
                              const candidates = taskListState.filter(
                                (t) => t.taskId === taskId
                              );
                              const task =
                                candidates.find((t) => t.isOpen) ||
                                candidates[0];
                              const resolvedTitle =
                                title || getTitleFromMeta(taskId);
                              const isDone = !!task?.isDone;
                              const isOpen = !!task?.isOpen;
                              const locked =
                                !isDone &&
                                !isOpen &&
                                !isAllAccessPricingUserExpired;
                              const taskKey =
                                task?.taskKey ||
                                `${taskId}:${patientState?.patientId}`;

                              return (
                                <ProgressItemRow
                                  key={taskId}
                                  taskKey={taskKey}
                                  isDone={isDone}
                                  isOpen={isOpen}
                                  locked={locked}
                                  title={resolvedTitle}
                                />
                              );
                            })}
                          </Stack>
                        )}
                      </Stack>
                    </Box>
                  ))}
              </Stack>
            )}

            {selectedTab === "goal" && (
              <Stack>
                <MyConcernAndGoal
                  concern={concernAndGoal?.concern}
                  goal={concernAndGoal?.goal}
                />
              </Stack>
            )}

            {selectedTab === "trend" && !isStarterPlan && (
              <Stack>
                {dashboardData?.selfCheckResult && (
                  <TestResult
                    {...(dashboardData.selfCheckResult as TestResultType)}
                  />
                )}
              </Stack>
            )}
          </Stack>
        </Box>
      </div>
    </>
  );
}
